import { c as _c } from "react/compiler-runtime";
import type { FC } from "react";
import styled from "styled-components";
import { useSetAtom } from "jotai";
import { format } from "date-fns";
import { faEdit, faEllipsisV, faTrashAlt } from "@fortawesome/pro-solid-svg-icons";
import STYLES from "@vita-learning/styles";
import type { FlashcardSetPopulated } from "@vita-learning/types";
import { localizeDate } from "~/client/utils";
import { editAtom } from "~/client/services";
import { formatSourceOutput } from "~/shared/services";
import { getErrorMessage } from "~/shared/utils";
import { CancelButton, DeleteButton } from "../../buttons";
import { Dropdown, type DropdownItemProps } from "../../lists";
import { Alert, modalAtom, useAlert, usePopupQueue } from "../../portals";
import { Icon, Tooltip } from "../../widgets";
import { useFlashcardSetsStore } from "../../stores";
import type { ItemProps } from "../Item";
export interface FlashcardItemProps extends ItemProps {
  readonly flashcardSet: FlashcardSetPopulated;
  readonly onCheck?: (f: FlashcardSetPopulated) => Promise<FlashcardSetPopulated>;
  readonly onDelete?: (f: FlashcardSetPopulated) => Promise<void>;
  readonly onEdit?: () => Promise<void>;
  readonly dateFormat?: string;
}
const useFlashcardItem = ({
  flashcardSet: f,
  dateFormat = "eee, MMM do",
  onCheck,
  onDelete,
  onEdit,
  onClick
}: FlashcardItemProps) => {
  const {
    enqueueInfo,
    enqueueError,
    enqueueSuccess
  } = usePopupQueue();
  const setEditId = useSetAtom(editAtom);
  const setModal = useSetAtom(modalAtom);
  const {
    onCancel: handleCancel,
    onOpen
  } = useAlert();
  const updateFlashcardSet = useFlashcardSetsStore(s => s.update);
  const deleteFlashcardSet = useFlashcardSetsStore(s_0 => s_0.remove);
  const handleClick = () => {
    if (f.id) setEditId(f.id);
    onClick?.();
  };
  const handleCheck = async () => {
    try {
      if (!f.id) throw new Error("Flashcard set ID was not found");
      const [toggledFlashcardSet] = await Promise.all([updateFlashcardSet({
        ...f,
        isCompleted: !f.isCompleted
      } as FlashcardSetPopulated), await onCheck?.(f as FlashcardSetPopulated)]);
      if (toggledFlashcardSet.isCompleted) enqueueSuccess(`'${f.title}' has been completed`, "Flashcard Set Completed");else enqueueInfo(`'${f.title}' has been reset`, "Flashcard Set	Reset");
    } catch (e) {
      enqueueError(getErrorMessage(e), "Error Checking Flashcard Set");
    }
  };
  const handleDelete = async () => {
    try {
      if (!f.id) throw new Error("Flashcard set ID was not found");
      await deleteFlashcardSet(f.id);
      onDelete?.(f as FlashcardSetPopulated);
      enqueueSuccess(`'${f.title}' has been deleted`);
    } catch (e_0) {
      enqueueError(getErrorMessage(e_0), "Error Deleting Flashcard Set");
    }
  };
  const dropdownItems: DropdownItemProps[] = [{
    label: "Edit",
    icon: faEdit,
    onClick: async () => {
      await onEdit?.();
      setModal("EDIT_FLASHCARD");
    }
  }, {
    label: "Delete",
    icon: faTrashAlt,
    onClick: f.id ? onOpen(f.id) : undefined
  }];
  const formattedDate = f.date && format(f.date, dateFormat);
  const formattedSource = f.source === "auto" || f.source === "manual" ? null : formatSourceOutput(f.source);
  return {
    formattedSource,
    formattedDate,
    dropdownItems,
    onDelete: handleDelete,
    onCheck: handleCheck,
    onClick: handleClick,
    onCancel: handleCancel
  };
};
export const FlashcardItem: FC<FlashcardItemProps> = t0 => {
  const $ = _c(48);
  let f;
  let props;
  if ($[0] !== t0) {
    ({
      flashcardSet: f,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = f;
    $[2] = props;
  } else {
    f = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== f || $[4] !== props) {
    t1 = {
      ...props,
      flashcardSet: f
    };
    $[3] = f;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    formattedDate,
    formattedSource,
    onClick,
    onDelete,
    onCheck,
    onCancel,
    dropdownItems
  } = useFlashcardItem(t1);
  let t2;
  if ($[6] !== f.title) {
    t2 = <Title>{f.title}</Title>;
    $[6] = f.title;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== f.isCompleted || $[9] !== t2) {
    t3 = <ColumnContainer $flex={4} $isCompleted={f.isCompleted}>{t2}</ColumnContainer>;
    $[8] = f.isCompleted;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== formattedDate) {
    t4 = <p>{formattedDate}</p>;
    $[11] = formattedDate;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== formattedSource) {
    t5 = <small>{formattedSource}</small>;
    $[13] = formattedSource;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== f.date || $[16] !== f.isCompleted || $[17] !== t4 || $[18] !== t5) {
    t6 = <ColumnContainer $flex={2} $isCompleted={f.isCompleted} $date={f.date}>{t4}{t5}</ColumnContainer>;
    $[15] = f.date;
    $[16] = f.isCompleted;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const t7 = f.isCompleted ? "Undo completion" : "Mark completed";
  let t8;
  if ($[20] !== f.isCompleted || $[21] !== onCheck) {
    t8 = <Checkbox defaultChecked={f.isCompleted} onClick={onCheck} />;
    $[20] = f.isCompleted;
    $[21] = onCheck;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t7 || $[24] !== t8) {
    t9 = <Tooltip text={t7} placement="bottom-start">{t8}</Tooltip>;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Icon icon={faEllipsisV} />;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== dropdownItems) {
    t11 = <Dropdown items={dropdownItems}>{t10}</Dropdown>;
    $[27] = dropdownItems;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t11 || $[30] !== t9) {
    t12 = <ContentContainer>{t9}{t11}</ContentContainer>;
    $[29] = t11;
    $[30] = t9;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== onClick || $[33] !== t12 || $[34] !== t3 || $[35] !== t6) {
    t13 = <Container onClick={onClick}>{t3}{t6}{t12}</Container>;
    $[32] = onClick;
    $[33] = t12;
    $[34] = t3;
    $[35] = t6;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== onDelete) {
    t14 = <DeleteButton onClick={onDelete}>Delete</DeleteButton>;
    $[37] = onDelete;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== onCancel) {
    t15 = <CancelButton onClick={onCancel}>Cancel</CancelButton>;
    $[39] = onCancel;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== f.id || $[42] !== t14 || $[43] !== t15) {
    t16 = <Alert id={f.id} title="Are you sure you want to delete this flashcard set?" message="This action cannot be undone.">{t14}{t15}</Alert>;
    $[41] = f.id;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t13 || $[46] !== t16) {
    t17 = <>{t13}{t16}</>;
    $[45] = t13;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  return t17;
};
const Container = styled.div<{
  $isCompleted?: boolean;
}>`
	align-items: center;
	background-color: #FFF;
	border: 1px solid ${STYLES.colors.lights.borders.light};
	border-left: 1px solid ${STYLES.colors.lights.borders.light};
	border-radius: 5px;
	display: flex;
	font-weight: 500;
	margin-bottom: 0.5rem;
	max-width: 500px;
	margin: 0.5rem 0;
	padding: 0.5rem;
	transition: all 0.25s ease;	
`;
const getFlex = ({
  $flex
}: {
  $flex?: number;
}) => $flex;
const ContentContainer = styled.div<{
  $flex?: number;
}>`
	align-items: center;
	display: flex;
	
	flex: ${getFlex};
	margin: 0 0.5rem;
`;
const ColumnContainer = styled.div<{
  $date?: string;
  $isCompleted?: boolean;
  $flex?: number;
}>`
	align-items: stretch;
	display: flex;
	color: ${({
  $date,
  $isCompleted
}) => {
  if ($isCompleted) return STYLES.colors.lights.texts.striked;
  if (!$date) return STYLES.colors.lights.texts.normal;
  if (!$isCompleted && new Date($date) < localizeDate()) return STYLES.colors.lights.backgrounds.dangers.normal;
  return STYLES.colors.lights.texts.normal;
}};
	flex: ${getFlex};
	flex-direction: column;
	font-size: ${({
  $isCompleted
}) => $isCompleted !== undefined ? "0.875rem" : "1rem"};
	justify-content: space-around;

`;
const Title = styled.h4`
	margin: 0;
`;
const Checkbox = styled.input.attrs(() => ({
  type: "checkbox"
}))`
	cursor: pointer;
	position: relative;
	top: 1px;
`;
export default FlashcardItem;